import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { CatalogLazyLoader, ILoaderState } from '@df/catalog-ui/lazy-loader/catalog-lazy-loader';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Status } from '@df/core/status';
import { StickyDirective } from '@df/legacy/ui/sticky/sticky.directive';
import { InfiniteScrollModule } from '@df/module-infinite-scroll/infinite-scroll.module';
import { FilterKlevuSearch } from '@df/module-klevu-search/filter/filter-klevu-search';
import { surviveTreeShaking } from '@df/ng/survive-tree-shaking';
import { SearchService } from '@df/search/search.service';
import { BulkOrder } from '@df/session/api/basket/bulk-order/bulk-order';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import difference from 'lodash-es/difference';
import { BehaviorSubject } from 'rxjs';
import { HEADER_ID } from '../../layout/page/header/page-header.component';
import { EGtmAddToCartVia, LocalGtmService } from '../../module/gtm/gtm.service.decorator';
import { VipService } from '../../module/vip/vip.service';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/resut/result.component';
import { SkeletonLoaderComponent } from '../../ui/skeleton-loader/skeleton-loader.component';
import { BulkOrderListingProductComponent } from './bulk-order-listing-product.component';

surviveTreeShaking(FilterKlevuSearch);
@Component({
    selector: 'bulk-order-listing',
    templateUrl: 'bulk-order-listing.html',
    standalone: true,
    imports: [
        StickyDirective,
        ToggleModule,
        FormsModule,
        TranslationModule,
        DfUiModule,
        PriceComponent,
        SizeClassDirective,
        ActionComponent,
        ResultComponent,
        InfiniteScrollModule,
        BulkOrderListingProductComponent,
        SkeletonLoaderComponent,
        ActionComponent,
        ResultComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            .va-m {
                vertical-align: middle !important;
            }
        `
    ],
    providers: [
        {
            provide: CatalogLazyLoader,
            useClass: CatalogLazyLoader<Product<IProductDataV2>>
        }
    ]
})
export class BulkOrderListingComponent extends UiComponent implements OnInit {
    @Input()
    bulk: BulkOrder;

    protected readonly submissionStatus = new Status();
    protected loaderState: ILoaderState;
    protected _models: Product<IProductDataV2>[] = [];
    protected headerId = HEADER_ID;

    protected readonly loader = inject(CatalogLazyLoader<Product<IProductDataV2>>);
    protected readonly productResource = inject(ProductResource);
    protected readonly hashStorageService = inject(HashStorageService);
    protected readonly afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    private gtmService = inject(LocalGtmService);
    private vipService = inject(VipService);

    private processedModelsIds: number[] = [];
    protected listWarning?: string;

    private override: Product<IProductDataV2>[] = [];
    private _overrideContext?: 'selection' | 'search';

    protected searchBusy$ = new BehaviorSubject(false);

    protected searchTerm: string;
    private searchService = inject(SearchService);
    protected asyncCd = false;
    // this.searchService.search({ q: this.term, ...this.config.options.search }).then(result => this.onSearchResult(result))

    ngOnInit(): void {
        this.afterContentInitDirectiveService.lock(this);
        const loadFilter = {
            url: { $exists: true },
            parent: { $exists: false }
        };
        this.subscriptions.add(this.bulk.change.subscribe(() => this.detectChanges()));
        this.loader.init({
            verbosity: 2,
            type: 'product',
            pageSize: 50,
            getFilter: (query?: string) => {
                const filter: any = { ...(loadFilter || {}) };
                if (query) {
                    const matcher = { $regex: query, $options: 'i' };
                    const fields = ['name', 'sku'];
                    filter.$or = fields.map(name => ({ [`data.${name}`]: matcher }));
                }
                return filter;
            },
            getSort: () => {
                return {
                    created_at: -1
                };
            },
            onModels: async models => {
                if (!this.searchTerm) {
                    models = models.filter(i => i.data && !i.data.parent);
                    await this.vipService.loadForProducts(models);
                    await this.loadChildrenFor(models);
                    this.setModels(models);
                    this.detectChanges();
                }
                this.afterContentInitDirectiveService.unlock(this);
            },
            onStateChange: state => {
                this.loaderState = state;
                this.detectChanges();
            }
        });
        super.ngOnInit();

        this.search(this.hashStorageService.get('search'));
    }

    showSelected() {
        const errors = this.bulk.getErroringItems() as Product<IProductDataV2>[];
        const selected = this.bulk.getSelectedItems() as Product<IProductDataV2>[];
        this.setModels([...errors, ...difference(selected, errors)], 'selection', 'The list shows only imported products.');
    }

    setModels(models: Product<IProductDataV2>[], override?: 'selection' | 'search', warning?: string) {
        this._overrideContext = override;

        if (!override || override === 'search') {
            models = models.filter(i => i.isSalable(1, undefined, false));
        }
        if (override) {
            this.override = models;
        } else {
            this._models = models;
        }
        if (!models?.length && !warning) {
            warning = 'No matching products found.';
        }
        this.listWarning = warning;
        this.detectChanges();
    }

    restoreModels(fromOverride?: 'selection' | 'search') {
        if (!fromOverride || this._overrideContext === fromOverride) {
            this._overrideContext = undefined;
        }
        this.detectChanges();
    }

    get models(): Product<IProductDataV2>[] {
        return this._overrideContext ? this.override : this._models;
    }

    get overrideContext() {
        return this._overrideContext;
    }

    get qty() {
        if (this._overrideContext) {
            return this.override?.length || 0;
        }
        return 0;
    }

    protected onSearchKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.search();
        }
    }

    private async search(term = this.searchTerm) {
        if (term?.length > 2) {
            this.searchTerm = term;
            this.hashStorageService.set('search', term);
            this.searchBusy$.next(true);
            const result = await this.searchService.search({ q: this.searchTerm, autocomplete: false, product: 100 });
            if (term === this.searchTerm) {
                const products = result.products?.filter(i => i.data) || [];
                // const parents = uniq(products.map(i => i.data.parent));
                // if(parents.length) {
                //     const parentModels = await this.productResource.loadAllIds(parents, 2, {safe : true});
                // }
                await this.loadChildrenFor(products);
                await this.vipService.loadForProducts(products);
                this.setModels(products, 'search');
                this.searchBusy$.next(false);
            }
        } else {
            this.resetSearch();
        }
    }

    protected resetSearch() {
        this.searchTerm = undefined;

        this.hashStorageService.remove('search');
        if (this.searchBusy$.value) {
            this.searchBusy$.next(false);
        }
        this.restoreModels('search');
    }

    // protected override getFilterDefs(): (IFilterDefinition | string)[] {
    //     return [{ type: KLEVU_SEARCH_FILTER_TYPE }, ...super.getFilterDefs()];
    // }
    // protected override getSortOps(): IListingSortOption[] | undefined {
    //     return [this.getAppConfig().sort[0], { label: 'Favourites', engine: 'favourites' }, ...this.getAppConfig().sort.slice(1)];
    // }

    protected get canLoadMore(): boolean {
        return this.loaderState.canLoadMore && !this._overrideContext;
    }

    protected get canShowLoader(): boolean {
        return !this.loaderState.isFullyLoaded && !this._overrideContext;
    }

    protected submit() {
        const productsWithErrors = this.bulk.getErroringItems();
        if (productsWithErrors.length) {
            this.submissionStatus.error = 'Please review errors below';
            this.setModels(productsWithErrors as Product<IProductDataV2>[], 'selection', 'The list shows only products with errors.');
        } else {
            this.bulk.submit(this.submissionStatus, result => this.gtmService.gtmAddToCartBeEvent(result, EGtmAddToCartVia.trade));
        }
    }

    private async loadChildrenFor(models: Product[]) {
        // load children for visible parents
        const allChildrenIds = [];
        models?.forEach(model => {
            if (model.isConfigurable && !this.processedModelsIds.includes(model.id)) {
                this.processedModelsIds.push(model.id);
                const childrenIds = model.childrenIds;
                if (childrenIds.length) {
                    allChildrenIds.push(...childrenIds);
                }
            }
        });
        if (allChildrenIds?.length) {
            const children = await this.productResource.loadAllIds(allChildrenIds, 2, { safe: true });
            await this.vipService.loadForProducts(children);
        }
    }
}
